/**
 * /settings/agent-identities — liste agents IA / systèmes externes (9.M.7).
 *
 * Surface super_admin only. Pattern parité BrokerListPage 9.P.3.C : sticky
 * header + filter bar (kind + include_disabled) + table moderne ≥md + cards
 * stack <md + states distincts (loading / empty / no-match / error retry).
 *
 * RBAC client : redirect `/` si user.role !== 'super_admin' (defense-in-depth
 * vs RLS DB qui rejette les écritures `is_super_admin()` only).
 *
 * Pas de scope tenant_admin Phase 1 — voir runbook 9.M.7 §RBAC.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";

import { Pagination, usePagination } from "../../../components/Pagination";
import { useAuth } from "../../../contexts/AuthContext";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  useAgentIdentities,
  type AgentIdentitiesListParams,
} from "../../../lib/queries/agentIdentities";

import { CreateAgentDialog } from "./_components/CreateAgentDialog";

type KindFilter = "all" | "ai_operator" | "external_system";
const KIND_OPTIONS: KindFilter[] = ["all", "ai_operator", "external_system"];

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

export function AgentIdentitiesListPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const pagination = usePagination();
  const [kind, setKind] = useState<KindFilter>("all");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const params: AgentIdentitiesListParams = {
    cursor: pagination.cursor,
    kind: kind === "all" ? undefined : kind,
    include_disabled: includeDisabled,
  };

  const { data, isLoading, isFetching, error, refetch } = useAgentIdentities(params);

  if (user && user.role !== "super_admin") {
    return <Navigate to="/" replace />;
  }

  const onKindChange = (next: KindFilter) => {
    setKind(next);
    pagination.reset();
  };
  const onToggleDisabled = (next: boolean) => {
    setIncludeDisabled(next);
    pagination.reset();
  };
  const onResetFilters = () => {
    setKind("all");
    setIncludeDisabled(false);
    pagination.reset();
  };

  const hasFilters = kind !== "all" || includeDisabled;
  const showSkeleton = isLoading && !data;
  const showEmpty = !!data && data.items.length === 0 && !hasFilters && !error;
  const showNoMatch = !!data && data.items.length === 0 && hasFilters && !error;

  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:-mx-8 lg:top-0 lg:px-8 lg:py-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              {t("agent_identities.list.title")}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {t("agent_identities.list.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
          >
            <span aria-hidden="true">+</span>
            {t("agent_identities.list.create_cta")}
          </button>
        </div>
      </header>

      <div className="px-0 py-6 lg:py-8">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <label className="text-sm text-slate-600">
            <span className="sr-only">{t("agent_identities.list.filters.kind")}</span>
            <select
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              value={kind}
              onChange={(e) => onKindChange(e.target.value as KindFilter)}
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {t(`agent_identities.list.kinds.${k}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeDisabled}
              onChange={(e) => onToggleDisabled(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            {t("agent_identities.list.filters.include_disabled")}
          </label>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            <span>
              {error instanceof ApiException
                ? getApiErrorMessage(error, lang) || t("errors.generic")
                : t("errors.generic")}
            </span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              {t("common.retry")}
            </button>
          </div>
        )}

        {showSkeleton && (
          <div
            aria-live="polite"
            className="space-y-2 rounded-xl border border-slate-200 bg-white p-4"
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-10 rounded-md bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none"
              />
            ))}
          </div>
        )}

        {showEmpty && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
            <h2 className="text-base font-semibold text-slate-900">
              {t("agent_identities.list.empty_state.title")}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
              {t("agent_identities.list.empty_state.subtitle")}
            </p>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
            >
              <span aria-hidden="true">+</span>
              {t("agent_identities.list.create_cta")}
            </button>
          </div>
        )}

        {showNoMatch && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <strong className="block font-semibold">
                  {t("agent_identities.list.no_match.title")}
                </strong>
                <span className="text-amber-700">
                  {t("agent_identities.list.no_match.subtitle")}
                </span>
              </div>
              <button
                type="button"
                onClick={onResetFilters}
                className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
              >
                {t("agent_identities.list.no_match.reset")}
              </button>
            </div>
          </div>
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white md:block">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">
                      {t("agent_identities.list.columns.display_name")}
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      {t("agent_identities.list.columns.kind")}
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      {t("agent_identities.list.columns.scope")}
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      {t("agent_identities.list.columns.status")}
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      {t("agent_identities.list.columns.created_at")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/settings/agent-identities/${a.id}`}
                          className="rounded-sm font-medium text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                        >
                          {a.display_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                          {t(`agent_identities.kinds.${a.kind}`, { defaultValue: a.kind })}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {a.tenant_id === null
                          ? t("agent_identities.scope.global")
                          : t("agent_identities.scope.tenant")}
                      </td>
                      <td className="px-4 py-3">
                        {a.disabled_at ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200">
                            <span
                              aria-hidden="true"
                              className="h-1.5 w-1.5 rounded-full bg-red-500"
                            />
                            {t("agent_identities.status.disabled")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                            <span
                              aria-hidden="true"
                              className="h-1.5 w-1.5 rounded-full bg-emerald-500"
                            />
                            {t("agent_identities.status.active")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="space-y-3 md:hidden">
              {data.items.map((a) => (
                <li key={a.id} className="rounded-xl border border-slate-200 bg-white p-4">
                  <Link
                    to={`/settings/agent-identities/${a.id}`}
                    className="block rounded-sm text-sm font-medium text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                  >
                    {a.display_name}
                  </Link>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <dt className="text-slate-500">
                        {t("agent_identities.list.columns.kind")}
                      </dt>
                      <dd className="mt-0.5 text-slate-700">
                        {t(`agent_identities.kinds.${a.kind}`, { defaultValue: a.kind })}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">
                        {t("agent_identities.list.columns.status")}
                      </dt>
                      <dd className="mt-0.5">
                        {a.disabled_at
                          ? t("agent_identities.status.disabled")
                          : t("agent_identities.status.active")}
                      </dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <Pagination
                pageNumber={pagination.pageNumber}
                hasPrev={pagination.hasPrev}
                hasNext={!!data.next_cursor}
                onPrev={pagination.prev}
                onNext={() => pagination.next(data.next_cursor ?? null)}
                disabled={isFetching}
              />
            </div>
          </>
        )}
      </div>

      {createOpen && <CreateAgentDialog onClose={() => setCreateOpen(false)} />}
    </div>
  );
}
